/**
 * CLE-2661 — pVerify practice type codes offered in the Service Type dropdown.
 *
 * A practice type code selects the SET of service types pVerify asks the payer about. The numbers are
 * easy to confuse with the X12 service type codes that appear inside pVerify's own labels, and getting
 * them backwards is silent: this list used to offer "ASC Facility" as `13`, which is the SERVICE type
 * for ASC but resolves as a PRACTICE type to Hospital-InPatient. Every response to it came back
 * without an ASC block at all — strictly less ASC data than the default option returns. The practice
 * type code for a plain ASC inquiry is `28`.
 *
 * Kept short on purpose. pVerify publishes 158 codes; a dropdown of mostly-irrelevant options is how
 * the wrong one gets picked. Mirrors PRACTICE_TYPE_CATALOG in the API's eligibility domain — keep the
 * two in step.
 *
 * CLE-2675 (2026-08-25) — these entries say "Surgical (Physician Benefit)" for pVerify's
 * `Surgical - Office (2)`. It replaced "Physician Outpatient", which collided with the benefits
 * tab of that name (a different block) and cost the estimator an afternoon. Ani's wording, and
 * deliberately the SAME wording as the tab and the account benefit-priority setting — one name,
 * one benefit. See the fuller note on PRACTICE_TYPE_CATALOG in the API.
 */

/**
 * The historical global default: ASC Facility (13) + Hospital-InPatient (48) +
 * Hospital-OutPatient (50) + Surgical-Office (2), all in one inquiry. Used when the account has no
 * `defaultPracticeTypeCode` of its own.
 */
export const DEFAULT_PRACTICE_TYPE_CODE = "189";

export const mapPayerNameToCodeForDropdown: { name: string; value: string }[] =
  [
    {
      name: "All Other Medical (ASC + Hospital In/Outpatient + Surgical (Physician Benefit))",
      value: "189",
    },
    {
      name: "Hospital Outpatient + Surgical (Physician Benefit)",
      value: "235",
    },
    { name: "ASC Facility + Surgical (Physician Benefit)", value: "185" },
    // CLE-2675 — Specialist Office (98) + Surgical (2) together. Office-based accounts on the
    // payer-routed benefit priority need both blocks on one response: the office block for
    // Medicare Advantage, the Surgical block for commercial. Code 18 returns only the first.
    { name: "Specialist Office + Surgical (Physician Benefit)", value: "43" },
    { name: "ASC Facility", value: "28" },
    { name: "Hospital Outpatient", value: "83" },
    { name: "Specialist - Office", value: "18" },
    { name: "X-Ray + MRI/CAT Scan", value: "46" },
    { name: "MRI/CAT Scan", value: "31" },
    // CLE-2755 — Ani, 2026-09-08. Lets an estimator mark an order as a diagnostic study rather
    // than a procedure. On Lincoln's BCBS Medicare Advantage TN/AL that selection is what drops
    // the quoted copay from the $325 procedure rate to the $100 diagnostic one; the API rule is
    // `getBcbsDiagnosticCopayOverride`. CLE-2989 — pVerify's practice type is 113. This was 73 —
    // the X12 SERVICE type for Diagnostic Medical — which pVerify silently ran as
    // Hospital-InPatient; 73 survives only as LEGACY_PRACTICE_TYPE_ALIASES below.
    { name: "Diagnostic Medical", value: "113" },
  ];

/**
 * The practice type an eligibility call should default to: the account's own setting when it has one,
 * otherwise the historical global default. Every account starts with none, so nothing changes until an
 * account is moved over deliberately.
 */
export const getDefaultPracticeTypeCode = (
  accountDefaultPracticeTypeCode?: string | null
): string => accountDefaultPracticeTypeCode || DEFAULT_PRACTICE_TYPE_CODE;

const KNOWN_PRACTICE_TYPE_CODES = new Set(
  mapPayerNameToCodeForDropdown.map((option) => option.value)
);

export const isKnownPracticeTypeCode = (code?: string | null): boolean =>
  !!code && KNOWN_PRACTICE_TYPE_CODES.has(code);

/**
 * CLE-2989 — codes we once sent that never meant what we thought, mapped to the code that does.
 * Mirrors LEGACY_PRACTICE_TYPE_ALIASES in the API's practiceTypes.ts — keep the two in step.
 *
 * 73 is the X12 service type code for Diagnostic Medical; sent as a pVerify practice type (CLE-2755)
 * it ran Hospital-InPatient. The practice type is 113. Orders saved with 73 keep their Diagnostic
 * Medical meaning through this map rather than a data migration; the API applies the same map, so
 * the stored code becomes 113 the next time the order's eligibility runs.
 *
 * Not for 13 (the ASC mistake): that one is retired and falls back to the account default.
 */
export const LEGACY_PRACTICE_TYPE_ALIASES: Readonly<Record<string, string>> =
  Object.freeze({ "73": "113" });

const normalizePracticeTypeCode = (
  code?: string | null
): string | null | undefined =>
  code &&
  Object.prototype.hasOwnProperty.call(LEGACY_PRACTICE_TYPE_ALIASES, code)
    ? LEGACY_PRACTICE_TYPE_ALIASES[code]
    : code;

/**
 * CLE-2661 — a practice type code carried over from an existing order, or null when that code is no
 * longer one we offer.
 *
 * Redo / copy / draft seed the Service Type from the order's own recorded code so a repeat inquiry
 * asks the same question the original did. That is right for codes we still offer and wrong for the
 * ones we removed: `13` in particular is not a practice type at all — pVerify resolves it to
 * Hospital-InPatient and answers with the hospital ambulatory-surgical benefit rather than the
 * freestanding-ASC one. Roughly 370 production orders carry it.
 *
 * Carrying such a code forward is worse than merely stale, because the estimator cannot correct it:
 * a PrimeReact Dropdown holding a value absent from its options renders as the empty placeholder, so
 * the form looks blank while silently still holding the bad code. Returning null instead lets
 * PrimaryInsuranceSection / SecondaryInsuranceSection fill in the account's default, which is what a
 * brand-new order on that account would use.
 *
 * Scoped to the SPA on purpose. The monthly re-run still re-asks with the order's own recorded code
 * (`buildEligibilityRequestForOrder`), because changing the question for an estimate a patient has
 * already been sent moves their quote — a deliberate call, tracked separately. A redo or copy is a
 * NEW estimate, which is exactly the case CLE-2661 asks us not to create with the wrong code.
 *
 * CLE-2989 — a legacy code with an alias (73 → 113) is NOT dropped: it is returned as its alias
 * target, because unlike 13 it had exactly one meaning (Diagnostic Medical) and the estimator chose it.
 */
export const knownPracticeTypeCodeOrNull = (
  code?: string | null
): string | null => {
  const normalized = normalizePracticeTypeCode(code);
  return isKnownPracticeTypeCode(normalized) ? (normalized as string) : null;
};
